import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { asyncScheduler, Observable, scheduled } from 'rxjs';

import { WizardStep } from '../../../../../shared/controls/wizard-control/wizard-step.interface';

import { ErrorService } from '../../../../../shared/services/error-service';
import { ToastService } from '../../../../../shared/toast/toast.service';
import { ImportSalesService } from '../../../../../shared/services/import-sales-service';
import { DialogService } from '../../../../../shared/controls/message-dialog/message-dialog.service';
import { LoadingService } from '../../../../../shared/services/loading.service';
import { buildFilteredErrors, formatErrorValue } from '../../../../../shared/helpers/import-error-helper';

import { SalesBatchResult, SalesService, SalesTotalsResult, SampleSalesDataResult } from '../../../../../../service/proxy';

@Component({
    selector: 'sales-import-data',
    templateUrl: './sales-import-data.component.html',
    styleUrls: ['./sales-import-data.component.scss'],

    imports: [
        CommonModule,
        FormsModule
    ],

    providers: [
        SalesService,
    ]
})
export class SalesImportDataComponent implements OnInit, OnDestroy, WizardStep {
    @ViewChild('form')
    form!: NgForm;

    public batchId: number = 0;

    public batch: SalesBatchResult = new SalesBatchResult();
    public totals: SalesTotalsResult = new SalesTotalsResult();
    public sampleData: SampleSalesDataResult = new SampleSalesDataResult();

    public importInProgress: boolean = false;
    public importProgress: number = 0;
    public importCompleted: boolean = false;
    public importErrors: any[] = []
    public showErrors = false;
    public totalErrorCount: number = 0;
    public errorRowCount: number = 0;
    public totalRows: number = 0;
    public rowsImported: number = 0;

    public loading: boolean = false;
    public loadingTotals: boolean = false;

    public showData = false;
    public headers: string[] = [];
    public filteredErrors: any[] = [];

    constructor(
        private route: ActivatedRoute,
        private salesService: SalesService,
        private importSalesService: ImportSalesService,
        private toastService: ToastService,
        private errorService: ErrorService,
        private cdr: ChangeDetectorRef,
        private router: Router,
        private dialogService: DialogService,
        private loadingService: LoadingService
    ) {}

    ngOnInit(): void {
        // Data loaded in onActivate method
    }

    ngOnDestroy(): void {
        if (this.importSalesService) {
            this.importSalesService.stopListening();
        }
    }

    processErrors(): void {
        this.filteredErrors = buildFilteredErrors(this.importErrors, 10);
    }

    formatErrorValue(value: any): string {
        return formatErrorValue(value, 120);
    }

    onActivate(): void {
        this.route.paramMap.subscribe(params => {
            this.batchId = Number(params.get('id'));

            if (this.batchId > 0) {
                const mappingsChangedKey = `sales_mappings_changed_${this.batchId}`;
                const mappingsChanged = sessionStorage.getItem(mappingsChangedKey) === 'true';

                if (mappingsChanged) {
                    sessionStorage.removeItem(mappingsChangedKey);
                    this.deleteExistingDataAndImport();
                    return;
                }

                this.loadExistingBatchData();
            }
        });
    }

    loadExistingBatchData(): void {
        this.loadingTotals = true;
        this.loadingService.show();
        this.cdr.detectChanges();

        this.salesService.calculateSalesTotals(this.batchId)
            .subscribe({
                next: (response) => {
                    this.totals = response;
                    this.loadingTotals = false;
                    this.loadingService.hide();

                    const alreadyImported = (this.totals?.totalRowsImported || 0) > 0;

                    if (alreadyImported) {
                        this.importCompleted = true;
                        this.importProgress = 100;
                        this.importInProgress = false;
                        this.cdr.detectChanges();

                        return;
                    }

                    this.deleteExistingDataAndImport();
                },
                error: (error) => {
                    this.loadingTotals = false;
                    this.loadingService.hide();
                    this.errorService.handleError(error);
                }
            });
    }

    deleteExistingDataAndImport(): void {
        this.importInProgress = true;
        this.importCompleted = false;
        this.importProgress = 0;
        this.importErrors = [];
        this.filteredErrors = [];
        this.showErrors = false;
        this.showData = false;
        this.cdr.detectChanges();

        this.salesService.deleteSalesDataByBatchId(this.batchId)
            .subscribe({
                next: () => {
                    this.startImport();
                },
                error: (error) => {
                    this.importInProgress = false;
                    this.cdr.detectChanges();
                    this.errorService.handleError(error);
                }
            });
    }

    startImport(): void {
        this.importInProgress = true;
        this.importCompleted = false;
        this.cdr.detectChanges();

        this.importSalesService.startListening(this.batchId, (entry) => {
                if (entry) {
                    this.importProgress = entry.progress;
                    this.importErrors = entry.errors || [];
                    this.totalErrorCount = entry.totalErrorCount || entry.errors?.length || 0;
                    this.errorRowCount = entry.errorRowCount || 0;
                    this.totalRows = entry.totalRows || 0;
                    this.rowsImported = entry.rowsImported || 0;
                    this.showErrors = this.importErrors.length > 0;
                    this.processErrors();
                    this.cdr.detectChanges();
                }
                else {
                    this.toastService.show('No progress data received.', 'Info');
                }

                if (this.importProgress === 100 || entry.message === 'Import completed.') {
                    this.importInProgress = false;
                    this.importErrors = entry.errors || this.importErrors;
                    this.totalErrorCount = entry.totalErrorCount || entry.errors?.length || 0;
                    this.errorRowCount = entry.errorRowCount || 0;
                    this.totalRows = entry.totalRows || 0;
                    this.rowsImported = entry.rowsImported || 0;
                    this.processErrors();
                    this.cdr.detectChanges();

                    setTimeout(() => {
                        this.importInProgress = false;
                        this.importCompleted = true;

                        this.cdr.detectChanges();
                        this.getTotals();
                        this.showData = false;
                    }, 3000);
                }
            },
            () => {
                this.importInProgress = false;

                this.cdr.detectChanges();
                this.toastService.show('Failed to start sales data import.', 'Error');
            });
    }

    onNext(): Observable<boolean> {
        return new Observable<boolean>(observer => {
            observer.next(true);
            observer.complete();
        });
    }

    onPrevious(): Observable<boolean> {
        // Reset completion flag so returning to this step will re-import
        this.importCompleted = false;
        return scheduled([true], asyncScheduler);
    }

    isValid(): boolean {
         return this.importErrors.length === 0;
    }

    getTotals(): void {
        this.loadingTotals = true;
        this.loadingService.show();
        this.cdr.detectChanges();

        this.salesService.calculateSalesTotals(this.batchId)
            .subscribe({
                next: (response) => {
                    this.loadingTotals = false;
                    this.loadingService.hide();
                    this.totals = response;
                    this.cdr.detectChanges();
                },
                error: (error) => {
                    this.loadingTotals = false;
                    this.loadingService.hide();
                    this.errorService.handleError(error);
                }
            }
        );
    }

    toggleSampleData(): void {
        if (!this.showData) {
           if (!this.sampleData ||
                !Array.isArray(this.sampleData.sampleRows) ||
                this.sampleData.sampleRows.length === 0)
            {
                this.getSampleData();
            }
            else {
                this.showData = true;
                this.cdr.detectChanges();
            }
        }
        else {
            this.showData = false;
            this.cdr.detectChanges();
        }
    }

    getSampleData(): void {
        this.loading = true;
        this.salesService.getSalesSampleData(this.batchId)
            .subscribe({
                next: (response) => {
                    this.sampleData = response;

                    if (response.sampleRows && response.sampleRows.length > 0) {
                        this.headers = Object.keys(response.sampleRows[0]);
                    }
                    this.showData = true;
                    this.cdr.detectChanges();
                },
                error: (error) => {
                    if (error.status === 400) {
                        this.toastService.show('Bad request. Please try again.', 'Info');
                    }
                    else if (error.status === 404) {
                        this.toastService.show('No sample data found for this batch.', 'Info');
                    }
                    else {
                        this.toastService.show('Failed to fetch sample batch data. Please try again.', 'Error');
                        this.errorService.handleError(error);
                    }
                },
                complete: () => {
                    this.loading = false;
                    this.cdr.detectChanges();
                }
            });
    }

    formatNumber(value: number): string {
        if (!value) {
            return '0';
        }

        return value.toLocaleString('en-US');
    }

    formatHeader(header: string): string {
        let formatted = header.replace(/_/g, ' ');

        formatted = formatted.replace(/([A-Z])/g, ' $1');
        formatted = formatted.trim();
        formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);

        return formatted;
    }

    toggleError() {
        this.showErrors = !this.showErrors;
    }

    deleteSalesBatch(): void {
        if (!this.batchId) {
            this.toastService.show('No batch selected to delete.', 'Error');
            return;
        }
        this.dialogService.show('Delete Pending Batch', 'Are you sure you want to delete this batch?', 'Warning')
            .subscribe((confirmed: boolean) => {
                if (confirmed) {
                    this.salesService.deletePendingSalesBatch(this.batchId)
                        .subscribe({
                            next: () => {
                                this.toastService.show('Sales batch deleted successfully.', 'success');
                                this.router.navigate(['/sales-create']);
                            },
                            error: (error: any) => {
                                this.errorService.handleError(error);
                            }
                        }
                    );
                }
            }
        );
    }
}